import { useState } from 'react';
import { Alert, Button, Card, Icon, Table, cn, type Column } from '@evogard/ui';
import type { ManagementWeeks } from '@evogard/contract';
import { count, type ClaimFlowPeriod } from '@evogard/domain';
import { useManagementWeeks } from './useManagement.js';

/**
 * Os avisos por semana (26/10/2026): quantos sinistros foram abertos em cada
 * semana do período, de segunda a domingo, com o do terceiro empilhado sobre
 * o do associado.
 *
 * Toda semana aparece, a vazia também. A primeira e a última costumam estar
 * pela metade — o período começa e hoje termina no meio delas —, e vêm
 * apagadas, para a queda no fim não parecer uma semana fraca.
 */

type Week = ManagementWeeks['weeks'][number];
const totalOf = (w: Week) => w.member + w.thirdParty;

const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
function sundayOf(monday: string): string {
  const d = new Date(`${monday}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6);
  return d.toISOString().slice(0, 10);
}
const rangeOf = (w: Week) => `${ddmm(w.week)} a ${ddmm(sundayOf(w.week))}`;

export function ClaimWeeksChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementWeeks(days);
  const data = query.data;
  const total = data ? data.weeks.reduce((n, w) => n + totalOf(w), 0) : 0;

  return (
    <Card
      as="figure"
      title="Avisos por semana"
      descricao="Abertos por semana, de segunda a domingo; as semanas incompletas das pontas ficam mais claras."
      actions={
        total > 0 &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar os avisos por semana">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-56 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : total === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro aberto nos últimos {days} dias.
        </p>
      ) : asTable ? (
        <WeeksTable data={data} />
      ) : (
        <WeeksFigure data={data} total={total} />
      )}
    </Card>
  );
}

function WeeksFigure({ data, total }: { data: ManagementWeeks; total: number }) {
  const [hover, setHover] = useState<Week | null>(null);
  const max = Math.max(1, ...data.weeks.map(totalOf));
  /* Um rótulo a cada N semanas, para não amontoar no período de um ano. */
  const every = Math.ceil(data.weeks.length / 13);
  const full = data.weeks.filter((w) => !w.partial);
  const mean = full.length ? full.reduce((n, w) => n + totalOf(w), 0) / full.length : 0;
  const shown = hover ?? null;

  return (
    <div className="flex flex-col gap-3">
      <p className="min-h-5 text-sm text-texto-suave" aria-live="polite">
        {shown ? (
          <>
            Semana de {rangeOf(shown)}:{' '}
            <b className="font-semibold text-texto">
              {count(totalOf(shown), 'aviso', 'avisos')}
            </b>
            {shown.thirdParty > 0 && ` (${shown.thirdParty} de terceiro)`}
            {shown.partial && ' — semana incompleta'}
          </>
        ) : (
          <>
            <b className="font-semibold text-texto">{count(total, 'aviso', 'avisos')}</b>{' '}
            no período
            {full.length > 0 && (
              <> · média de {Math.round(mean * 10) / 10} por semana completa</>
            )}
          </>
        )}
      </p>
      <div
        className="flex h-36 items-end gap-1"
        role="img"
        aria-label={`Avisos por semana: ${data.weeks
          .map((w) => `semana de ${ddmm(w.week)}, ${totalOf(w)}`)
          .join('; ')}.`}
        onPointerLeave={() => setHover(null)}
      >
        {data.weeks.map((w) => (
          <div
            key={w.week}
            className="flex h-full min-w-0 flex-1 cursor-default flex-col justify-end"
            onPointerEnter={() => setHover(w)}
          >
            <div
              className={cn(
                'flex flex-col overflow-hidden rounded-t-[3px] transition-opacity',
                w.partial && 'opacity-45',
                hover && hover.week !== w.week && 'opacity-60',
              )}
              style={{ height: `${(totalOf(w) / max) * 100}%` }}
            >
              <span className="bg-texto-suave" style={{ flex: w.thirdParty }} />
              <span className="bg-primaria" style={{ flex: w.member }} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-1 border-t border-borda pt-1.5" aria-hidden>
        {data.weeks.map((w, i) => (
          <span
            key={w.week}
            className="min-w-0 flex-1 overflow-visible whitespace-nowrap text-[11px] text-texto-suave tabular-nums"
          >
            {i % every === 0 ? ddmm(w.week) : ''}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="size-2.5 rounded-[3px] bg-primaria" />
          associado
        </span>
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="size-2.5 rounded-[3px] bg-texto-suave" />
          terceiro
        </span>
      </div>
    </div>
  );
}

function WeeksTable({ data }: { data: ManagementWeeks }) {
  const columns: Column<Week>[] = [
    {
      title: 'Semana',
      cell: (r) => `${rangeOf(r)}${r.partial ? ' (incompleta)' : ''}`,
      alignment: 'inicio',
    },
    { title: 'Avisos', cell: totalOf, alignment: 'fim' },
    { title: 'Associado', cell: (r) => r.member, alignment: 'fim' },
    { title: 'Terceiro', cell: (r) => r.thirdParty, alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={data.weeks}
      rowKey={(r) => r.week}
      label={`Avisos por semana nos últimos ${data.days} dias`}
      empty="Nenhuma semana no período."
    />
  );
}
